import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentIcon from "@mui/icons-material/Assignment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import api from "../../services/api";
import useToastStore from "../../store/toastStore";
import LoadingSkeleton from "../../components/common/LoadingSkeleton";
import EmptyState from "../../components/admin/EmptyState";
import LocationOnIcon from "@mui/icons-material/LocationOn";

function LocationManagementPage() {
  const [tab, setTab] = useState("all"); // "all" | "unassigned"
  const [allLocations, setAllLocations] = useState([]);
  const [unassignedLocations, setUnassignedLocations] = useState([]);
  const [collectors, setCollectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("locationName");
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCollector, setSelectedCollector] = useState("");
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLocation, setDetailLocation] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const showToast = useToastStore((s) => s.showToast);

  // Fetch all locations from users
  const fetchAllLocations = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/users/role/user");
      const locations = [];
      res.data.forEach((user) => {
        if (user.locations) {
          user.locations.forEach((loc) => {
            locations.push({
              ...loc,
              ownerName: user.name,
              ownerEmail: user.email,
              ownerId: user.userId,
            });
          });
        }
      });
      setAllLocations(locations);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unassigned locations
  const fetchUnassignedLocations = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/users/locations/unassigned");
      setUnassignedLocations(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch collectors list
  const fetchCollectors = async () => {
    try {
      const res = await api.get("/api/users/role/collector");
      setCollectors(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAllLocations();
    fetchCollectors();
  }, []);

  useEffect(() => {
    if (tab === "unassigned") {
      fetchUnassignedLocations();
    }
  }, [tab]);

  const currentLocations = tab === "all" ? allLocations : unassignedLocations;

  // Filter and sort
  const filtered = useMemo(() => {
    let data = [...currentLocations];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (loc) =>
          (loc.locationName || "").toLowerCase().includes(q) ||
          (loc.address || "").toLowerCase().includes(q) ||
          (loc.ownerName || "").toLowerCase().includes(q)
      );
    }
    data.sort((a, b) => {
      const aVal = a[orderBy] || "";
      const bVal = b[orderBy] || "";
      if (order === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    return data;
  }, [currentLocations, search, orderBy, order]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(filtered.map((loc) => loc.locationId));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    if (!selectedCollector || selected.length === 0) return;
    try {
      await api.post(
        `/api/users/${selectedCollector}/assign-locations`,
        selected
      );
      showToast(
        `${selected.length}개 장소가 성공적으로 배정되었습니다.`,
        "success"
      );
      setAssignDialogOpen(false);
      setSelected([]);
      setSelectedCollector("");
      fetchAllLocations();
      if (tab === "unassigned") fetchUnassignedLocations();
    } catch (err) {
      showToast("배정에 실패했습니다.", "error");
    }
  };

  const handleViewDetail = async (loc) => {
    setDetailLocation(loc);
    setDetailDialogOpen(true);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/locations/${loc.locationId}/history`);
      setLocationHistory(res.data);
    } catch (err) {
      setLocationHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const renderWantItems = (loc) => {
    const items = [];
    if (loc.wantMixedPet) items.push("혼합 PET");
    if (loc.wantPet) items.push("일반 PET");
    if (loc.wantAluminum) items.push("알루미늄");
    if (loc.wantPaper) items.push("파지");
    if (items.length === 0) return <Chip label="미지정" size="small" variant="outlined" />;
    return items.map((item) => (
      <Chip key={item} label={item} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
    ));
  };

  const renderLocationDays = (loc) => {
    const days = loc.locationDay;
    if (!days || days.length === 0) return "-";
    return days.map((day) => (
      <Chip
        key={day}
        label={day}
        size="small"
        variant="outlined"
        color="primary"
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    ));
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">수거 장소 관리</Typography>
        {tab === "unassigned" && selected.length > 0 && (
          <Button
            variant="contained"
            startIcon={<AssignmentIcon />}
            onClick={() => setAssignDialogOpen(true)}
          >
            선택 장소 배정 ({selected.length})
          </Button>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setSelected([]);
            setPage(0);
          }}
        >
          <Tab
            label={`전체 장소 (${allLocations.length})`}
            value="all"
          />
          <Tab
            label={`미배정 장소 (${unassignedLocations.length})`}
            value="unassigned"
          />
        </Tabs>
      </Box>

      <TextField
        size="small"
        placeholder="장소명, 주소, 소유자 검색..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
        sx={{ mb: 2, width: 360 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <LoadingSkeleton variant="table" rows={8} />
      ) : filtered.length === 0 ? (
        <EmptyState
          message="조건에 맞는 장소가 없습니다."
          icon={LocationOnIcon}
        />
      ) : (
        <Paper sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {tab === "unassigned" && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selected.length > 0 &&
                          selected.length < filtered.length
                        }
                        checked={
                          selected.length === filtered.length &&
                          filtered.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "locationName"}
                      direction={
                        orderBy === "locationName" ? order : "asc"
                      }
                      onClick={() => handleSort("locationName")}
                    >
                      장소명
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>주소</TableCell>
                  {tab === "all" && <TableCell>소유자</TableCell>}
                  <TableCell>수거 요일</TableCell>
                  <TableCell>희망 품목</TableCell>
                  <TableCell align="center">상세</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered
                  .slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  .map((loc) => (
                    <TableRow
                      key={loc.locationId}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleViewDetail(loc)}
                    >
                      {tab === "unassigned" && (
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selected.includes(loc.locationId)}
                            onChange={() => handleSelect(loc.locationId)}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 500 }}>
                        {loc.locationName || "-"}
                      </TableCell>
                      <TableCell>{loc.address || "-"}</TableCell>
                      {tab === "all" && (
                        <TableCell>{loc.ownerName || "-"}</TableCell>
                      )}
                      <TableCell>{renderLocationDays(loc)}</TableCell>
                      <TableCell>{renderWantItems(loc)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="상세 보기">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetail(loc);
                            }}
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="페이지당 행"
          />
        </Paper>
      )}

      {/* 배정 다이얼로그 */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>수거업자에게 장소 배정</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selected.length}개의 장소를 선택한 수거업자에게 배정합니다.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>수거업자 선택</InputLabel>
            <Select
              value={selectedCollector}
              label="수거업자 선택"
              onChange={(e) => setSelectedCollector(e.target.value)}
            >
              {collectors.map((c) => (
                <MenuItem key={c.userId} value={c.userId}>
                  {c.name} ({c.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!selectedCollector}
          >
            배정하기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 상세 다이얼로그 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {detailLocation?.locationName || "장소 상세"}
        </DialogTitle>
        <DialogContent>
          {detailLocation && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {detailLocation.address}
              </Typography>

              {/* 수거 요일 */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  수거 요일
                </Typography>
                {renderLocationDays(detailLocation)}
                {detailLocation.locationTime && (
                  <Chip
                    label={`${detailLocation.locationTime}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>

              {/* 희망 품목 */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  희망 품목
                </Typography>
                {renderWantItems(detailLocation)}
              </Box>

              {/* 소유자 */}
              {detailLocation.ownerName && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    소유자
                  </Typography>
                  <Typography variant="body2">
                    {detailLocation.ownerName} ({detailLocation.ownerEmail})
                  </Typography>
                </Box>
              )}

              {/* 수거 이력 */}
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                수거 이력
              </Typography>
              {historyLoading ? (
                <LoadingSkeleton variant="table" rows={5} />
              ) : locationHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  수거 이력이 없습니다.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>날짜</TableCell>
                        <TableCell>수거자</TableCell>
                        <TableCell align="right">PET (kg)</TableCell>
                        <TableCell align="right">혼합PET (kg)</TableCell>
                        <TableCell align="right">알루미늄 (kg)</TableCell>
                        <TableCell align="right">파지 (kg)</TableCell>
                        <TableCell align="right">합계 (kg)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {locationHistory.slice(0, 20).map((record, idx) => {
                        const total =
                          (record.petWeight ?? 0) +
                          (record.mixedPetWeight ?? 0) +
                          (record.aluminumWeight ?? 0) +
                          (record.paperWeight ?? 0);
                        return (
                          <TableRow key={idx}>
                            <TableCell>
                              {record.collectedAt
                                ? new Date(record.collectedAt).toLocaleDateString()
                                : "-"}
                            </TableCell>
                            <TableCell>{record.collectorName || "-"}</TableCell>
                            <TableCell align="right">
                              {(record.petWeight ?? 0).toFixed(1)}
                            </TableCell>
                            <TableCell align="right">
                              {(record.mixedPetWeight ?? 0).toFixed(1)}
                            </TableCell>
                            <TableCell align="right">
                              {(record.aluminumWeight ?? 0).toFixed(1)}
                            </TableCell>
                            <TableCell align="right">
                              {(record.paperWeight ?? 0).toFixed(1)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {total.toFixed(1)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LocationManagementPage;
